import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, FormControl, InputLabel, Select, MenuItem, Switch, Typography,
  IconButton, Tooltip, Chip,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { getFeedDispatches, getVehicleColumns, getMpiWorkItemFields } from '../../actions/apiGetInfo';
import {
  addFeedDispatch, updateFeedDispatch, deleteFeedDispatch, persistDispatchOrder, cloneFeedDispatch,
} from '../../actions/apiFeedDispatches';
import {
  GROUP_DEFAULT, SELECTABLE_DISPATCH_TYPES, dispatchTypeLabel, PROPERTY_KEYS,
} from './dispatchConstants';
import DispatchEditorModal from './DispatchEditorModal';
import CloneDispatchDialog from './CloneDispatchDialog';

const LABEL_CHIP_PROPS = { clickable: false, onClick: () => {} };

const FeedDispatchRules = () => {
  const dispatch = useDispatch();
  const feed_dispatches = useSelector(s => s.settings_feed?.feed_dispatches || []);
  const departments = useSelector(s => s.settings_departments?.original_departments || []);
  const vehicle_columns = useSelector(s => s.settings_departments?.vehicle_columns || []);
  const mpi_work_item_fields = useSelector(s => s.settings_feed?.mpi_work_item_fields || []);
  const installation = useSelector(s => s.settings_dealerships?.installation || {});
  const related_installations = useSelector(s => s.settings_dealerships?.related_installations || []);
  const current_dealership = useSelector(s => s.settings_dealerships?.current_dealership || '');
  const currentUser = useSelector(s => s.current_user || {});
  const currentUserProfile = useSelector(s => s.settings_users?.current_user_profile || {});
  const dealer_settings = useSelector(s => s.settings_site?.dealer_settings || []);

  const canWrite = currentUser.position === 'super_admin' || currentUser.position === 'admin' || currentUserProfile.page_automatic_moves === true;

  const [scope, setScope] = useState(current_dealership || '');
  const [typeFilter, setTypeFilter] = useState('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cloneTarget, setCloneTarget] = useState(null);
  const [orderIds, setOrderIds] = useState([]);
  const dragId = useRef(null);

  useEffect(() => { dispatch(getFeedDispatches()); }, [dispatch]);
  useEffect(() => { if (!vehicle_columns.length) dispatch(getVehicleColumns()); }, [dispatch]);
  useEffect(() => { if (!mpi_work_item_fields.length) dispatch(getMpiWorkItemFields()); }, [dispatch]);
  useEffect(() => { if (current_dealership) setScope(prev => prev || current_dealership); }, [current_dealership]);

  const scopeDealership = scope === GROUP_DEFAULT ? '' : scope;

  const deptName = (id) => {
    if (id == null) return null;
    const d = departments.find(x => String(x.id != null ? x.id : x.department_id) === String(id));
    if (!d) return `#${id}`;
    return d.name_alias ? `${d.name} (${d.name_alias})` : d.name;
  };
  const deptExists = id => departments.some(x => String(x.id != null ? x.id : x.department_id) === String(id));

  const rows = useMemo(() => feed_dispatches
    .filter(d => (d.dealership || '') === (scopeDealership || ''))
    .filter(d => typeFilter === 'all' || (d.dispatch_type || 'feed_automove') === typeFilter)
    .slice()
    .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0) || (Number(a.position) || 0) - (Number(b.position) || 0)),
  [feed_dispatches, scopeDealership, typeFilter]);

  const dealershipOptions = useMemo(() => {
    const opts = [];
    if (current_dealership) opts.push({ value: current_dealership, label: `${current_dealership} (current)` });
    related_installations
      .filter(r => r.subdomain && r.subdomain !== current_dealership)
      .forEach(r => opts.push({ value: r.subdomain, label: r.softname || r.subdomain }));
    opts.push({ value: GROUP_DEFAULT, label: 'Group default' });
    return opts;
  }, [related_installations, current_dealership]);

  const settingOptions = useMemo(() => {
    const byKey = new Map();
    dealer_settings.forEach((r) => {
      const dr = r.dealership || '';
      if (dr !== '' && dr !== scopeDealership) return;
      const existing = byKey.get(r.key);
      if (!existing || (dr === scopeDealership && (existing.dealership || '') === '')) {
        byKey.set(r.key, { key: r.key, value: r.value, dealership: dr });
      }
    });
    let list = Array.from(byKey.values());
    const deptish = list.filter(o => /department|dept/i.test(o.key));
    if (deptish.length) list = deptish;
    return list.sort((a, b) => a.key.localeCompare(b.key));
  }, [dealer_settings, scopeDealership]);

  const scopeInstallationId = useMemo(() => {
    if (!scopeDealership) return null;
    if (installation && installation.subdomain === scopeDealership) return installation.id;
    const r = related_installations.find(x => x.subdomain === scopeDealership);
    return r ? r.id : null;
  }, [scopeDealership, installation, related_installations]);

  const propertyItems = useMemo(() => {
    const columnInScope = (c) => {
      const loc = String(c.location || '').split(',').map(s => s.trim()).filter(Boolean);
      if (loc.length === 0) return true;
      if (c.location_only !== true) return true;
      return scopeInstallationId != null && loc.includes(String(scopeInstallationId));
    };
    const byField = new Map();
    PROPERTY_KEYS.forEach(k => byField.set(k, { field: k, label: k }));
    vehicle_columns.forEach((c) => {
      if (!c || !c.name || !columnInScope(c)) return;
      byField.set(c.name, { field: c.name, label: c.display_name ? `${c.name} (${c.display_name})` : c.name });
    });
    return Array.from(byField.values())
      .map(o => ({ value: `property.${o.field}`, primary: o.label, secondary: `property.${o.field}` }))
      .sort((a, b) => a.primary.localeCompare(b.primary));
  }, [vehicle_columns, scopeInstallationId]);

  const workItemItems = useMemo(() => {
    const noise = /(_by_uid|_by_date|_editable|_locked|_inputtype|_options)$|^(label_|lock_|custom_header_|dvi_)/;
    const exclude = new Set(['id', 'vehicle_id', 'work_item_preset_id', 'deleted', 'created_at', 'updated_at', 'order', 'total_by_date', 'ro_files', 'recipients_by_uid', 'recipients_by_date', 'profile_dropdown_users']);
    const prettify = f => String(f).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return mpi_work_item_fields
      .filter(Boolean)
      .map(f => ({
        value: `work_item.${f}`,
        primary: prettify(f),
        secondary: `work_item.${f}`,
        relevant: !noise.test(f) && !exclude.has(f),
      }))
      .sort((a, b) => a.primary.localeCompare(b.primary));
  }, [mpi_work_item_fields]);

  const dynamicItems = useMemo(
    () => ({ property: propertyItems, work_item: workItemItems }),
    [propertyItems, workItemItems],
  );

  useEffect(() => { setOrderIds(rows.map(r => r.id)); }, [rows]);
  const orderedRows = useMemo(
    () => orderIds.map(id => rows.find(r => r.id === id)).filter(Boolean),
    [orderIds, rows],
  );

  const handleDragStart = (id) => { dragId.current = id; };
  const handleDragEnter = (id) => {
    if (dragId.current == null || dragId.current === id) return;
    setOrderIds((prev) => {
      const from = prev.indexOf(dragId.current);
      const to = prev.indexOf(id);
      if (from === -1 || to === -1) return prev;
      const next = prev.slice();
      next.splice(to, 0, next.splice(from, 1)[0]);
      return next;
    });
  };
  const handleDragEnd = () => {
    dragId.current = null;
    if (orderIds.join(',') === rows.map(r => r.id).join(',')) return;
    // Renumber the whole dealership scope, not just the rows the Type filter
    // shows: the reordered rows fill the slots they already held, so hidden
    // dispatches keep their place and priorities stay unique.
    const scoped = feed_dispatches
      .filter(d => (d.dealership || '') === (scopeDealership || ''))
      .slice()
      .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0) || (Number(a.position) || 0) - (Number(b.position) || 0));
    const byId = new Map(scoped.map(d => [d.id, d]));
    const reordered = orderIds.filter(id => byId.has(id));
    const visible = new Set(reordered);
    const full = scoped.map(d => (visible.has(d.id) ? byId.get(reordered.shift()) : d));
    const updates = full
      .map((d, i) => ({ d, data: { priority: i + 1, position: i } }))
      .filter(({ d, data }) => Number(d.priority) !== data.priority || Number(d.position) !== data.position)
      .map(({ d, data }) => ({ id: d.id, data }));
    if (updates.length) dispatch(persistDispatchOrder(updates, 'feedDispatchError'));
  };

  const cloneOptions = useMemo(
    () => dealershipOptions.filter(o => o.value !== GROUP_DEFAULT && o.value !== (cloneTarget ? (cloneTarget.dealership || '') : '')),
    [dealershipOptions, cloneTarget],
  );

  const handleClone = (targets) => {
    if (cloneTarget && targets.length) dispatch(cloneFeedDispatch(cloneTarget, targets, 'feedDispatchError'));
    setCloneTarget(null);
  };

  const openCreate = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (d) => { setEditing(d); setEditorOpen(true); };

  // Keep the saved dispatch visible: a new one switches the type filter to its
  // type; an edit only switches when the current filter would hide it.
  const showType = (type) => { if (type) setTypeFilter(type); };

  const handleSave = (payload, id) => {
    if (id) dispatch(updateFeedDispatch(id, payload, 'feedDispatchError'));
    else dispatch(addFeedDispatch(payload, 'feedDispatchError'));
    if (!id || (typeFilter !== 'all' && typeFilter !== payload.dispatch_type)) showType(payload.dispatch_type);
    setEditorOpen(false);
  };

  const toggleEnabled = d => dispatch(updateFeedDispatch(d.id, { enabled: !(d.enabled !== false) }, 'feedDispatchError'));

  const confirmDelete = () => {
    if (deleteTarget) dispatch(deleteFeedDispatch(deleteTarget.id, 'feedDispatchError'));
    setDeleteTarget(null);
  };

  return (
    <div className="card p-3" style={{ maxWidth: 1280, margin: '1rem auto' }}>
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2, mt: 2,
      }}
      >
        <Box>
          <Typography variant="h5">Feed Dispatch Rules</Typography>
          <Typography variant="body2" color="textSecondary">Route feed vehicles to departments. Lower priority order runs first (1 is first); the first matching dispatch wins. Drag rows to reorder.</Typography>
        </Box>
        <Box sx={{
          display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap',
        }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="feed-dispatch-type-filter-label">Type</InputLabel>
            <Select labelId="feed-dispatch-type-filter-label" label="Type" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <MenuItem value="all">All types</MenuItem>
              {SELECTABLE_DISPATCH_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
          {dealershipOptions.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="feed-dispatch-dealership-label">Dealership</InputLabel>
              <Select labelId="feed-dispatch-dealership-label" label="Dealership" value={scope} onChange={e => setScope(e.target.value)}>
                {dealershipOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {canWrite && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New Dispatch</Button>}
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="none" />
              <TableCell>Enabled</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Dealership</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Order</TableCell>
              <TableCell>Match</TableCell>
              <TableCell>Target</TableCell>
              <TableCell align="right">Rules</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>
                  <Box sx={{ py: 2, color: 'text.secondary' }}>No dispatches for this dealership yet.</Box>
                </TableCell>
              </TableRow>
            )}
            {orderedRows.map((d) => {
              let target = '—';
              if (d.target_department_id != null) target = deptName(d.target_department_id);
              else if (d.target_setting) {
                const opt = settingOptions.find(o => o.key === d.target_setting);
                const resolved = opt && opt.value ? deptName(opt.value) : null;
                target = resolved ? `${resolved} (setting: ${d.target_setting})` : `setting: ${d.target_setting}`;
              }
              const targetMissing = d.target_department_id != null && !deptExists(d.target_department_id);
              return (
                <TableRow
                  key={d.id}
                  hover
                  draggable={canWrite}
                  onDragStart={() => handleDragStart(d.id)}
                  onDragEnter={() => handleDragEnter(d.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={e => e.preventDefault()}
                >
                  <TableCell padding="none">
                    {canWrite && (
                      <Tooltip title="Drag to reorder">
                        <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'grab', verticalAlign: 'middle' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell><Switch size="small" checked={d.enabled !== false} disabled={!canWrite} onChange={() => toggleEnabled(d)} /></TableCell>
                  <TableCell>{d.name}</TableCell>
                  <TableCell>{d.dealership || 'Group default'}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={dispatchTypeLabel(d.dispatch_type)} {...LABEL_CHIP_PROPS} /></TableCell>
                  <TableCell align="right">{Number(d.priority) || 0}</TableCell>
                  <TableCell><Chip size="small" label={d.match_mode === 'any' ? 'ANY' : 'ALL'} {...LABEL_CHIP_PROPS} /></TableCell>
                  <TableCell>
                    {target}
                    {targetMissing && (
                      <Tooltip title="This department no longer exists; the dispatch is skipped.">
                        <Chip size="small" color="warning" label="missing" sx={{ ml: 1 }} {...LABEL_CHIP_PROPS} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">{(d.feed_dispatch_rules || []).length}</TableCell>
                  <TableCell align="right">
                    {canWrite ? (
                      <>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Clone to other dealerships"><IconButton size="small" onClick={() => setCloneTarget(d)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteTarget(d)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </>
                    ) : <Typography variant="caption" color="textSecondary">read-only</Typography>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <DispatchEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        dispatchItem={editing}
        dealership={scopeDealership}
        departments={departments}
        settingOptions={settingOptions}
        dynamicItems={dynamicItems}
        nextOrder={rows.length + 1}
        onSave={handleSave}
        onDispatchTypeChange={showType}
      />

      <CloneDispatchDialog
        open={!!cloneTarget}
        onClose={() => setCloneTarget(null)}
        dispatchItem={cloneTarget}
        options={cloneOptions}
        onConfirm={handleClone}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete dispatch?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            Remove &quot;
            {deleteTarget ? deleteTarget.name : ''}
            &quot; and its rules? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FeedDispatchRules;
